import { Form, Input, Select } from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalOutlined } from "@ant-design/icons";

import {
  IHouseStepAboutSpace,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";
import { HouseAPI } from "../../../../../API/houseApi";
import {
  HouseAccommodationValues,
  HouseAmountOfPeopleValues,
  THouseAccommodation,
  THousePrivateItems,
  THouseSharedItems
} from "../../../../../interfaces/interfaces";
import { PRIVATE_USE, SHARED_USE } from "../../../../../utils/constants";
import { WizardButtonSelect } from "../../../components";
import {
  Balcony,
  Bathroom,
  Garden,
  Kitchen,
  LivingRoom,
  Sink,
  StudyRoom,
  Toilet
} from "components/Icons/Wizard/AboutAccommodation/AllAboutAccommodation";
import {
  DoubleBed,
  Tv,
  WashingMachine,
  Wifi
} from "components/Icons/Wizard/FacilitiesAccommodation/AllFacilitiesAccommodation";
import { AccommodationCard } from "./AccommodationCard";
import { CardsContainer } from "../../../../containers";
import { Furnished, NonFurnished } from "../../../../Icons/house";

import { FormGroup } from "./styles";

const PRIVATE_ITEMS_ICONS: {
  [key in THousePrivateItems]: React.ReactNode;
} = {
  toilet: <Toilet />,
  bathroom: <Bathroom />,
  kitchen: <Kitchen />,
  "furnished room": <StudyRoom />,
  sink: <Sink />,
  internet: <Wifi />,
  "double bed": <DoubleBed />,
  balcony: <Balcony />,
  tv: <Tv />
};
const SHARED_ITEMS_ICONS: {
  [key in THouseSharedItems]: React.ReactNode;
} = {
  toilet: <Toilet />,
  bathroom: <Bathroom />,
  kitchen: <Kitchen />,
  livingroom: <LivingRoom />,
  "washing machine": <WashingMachine />,
  terrace: <GlobalOutlined />,
  balcony: <Balcony />,
  garden: <Garden />
};

const statePath = WIZARD_HOUSE_STEPS_PATHS.AboutSpace;
export const WizardStepAboutSpace: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepAboutSpace>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveAboutSpace(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveAboutSpacePartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepAboutSpaceView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const ACCOMMODATION_ICONS: {
  [key in THouseAccommodation]: React.ReactNode;
} = {
  furnished: <Furnished />,
  non_furnished: <NonFurnished />
};

export const WizardStepAboutSpaceView: TWizardStepComponentEditableView<IHouseStepAboutSpace> =
  React.memo(
    ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
      const { t } = useTranslation("house");
      const { t: thr } = useTranslation("hostrooms");

      const inputSize = "large";

      const accommodationOptions = HouseAccommodationValues.map((c) => ({
        value: c,
        label: t(`wizard.steps.AboutSpace.form.accommodation.options.${c}`),
        description: t(
          `wizard.steps.AboutSpace.form.accommodation.options_description.${c}`
        ),
        icon: ACCOMMODATION_ICONS[c]
      }));
      const amountOfPeopleOptions = HouseAmountOfPeopleValues.map((c) => ({
        value: c,
        label: t(`wizard.steps.AboutSpace.form.amount_of_people.options.${c}`)
      }));
      const privateUseOptions = PRIVATE_USE.map((c) => ({
        value: c,
        label: thr(`aboutTheRoom.privateUse.items.${c}.title`),
        icon: PRIVATE_ITEMS_ICONS[c]
      }));
      const sharedUseOptions = SHARED_USE.map((c) => ({
        value: c,
        label: thr(`aboutTheRoom.sharedUse.items.${c}.title`),
        icon: SHARED_ITEMS_ICONS[c]
      }));

      const isReadonly = createCheckIsReadonlyStepField(
        readonly,
        enabledFields
      );

      return (
        <Form layout="vertical">
          <Form.Item
            label={t(`wizard.steps.AboutSpace.form.accommodation.title`)}
            validateStatus={errors?.accommodation ? "error" : ""}
            help={errors?.accommodation ? t("wizard.errors.selection") : ""}
          >
            {viewonly ? (
              <div>
                {
                  accommodationOptions.find(
                    (v) => v.value === values.accommodation
                  )?.label
                }
              </div>
            ) : (
              <CardsContainer $gap="24px">
                {accommodationOptions.map((o) => (
                  <AccommodationCard
                    key={o.value}
                    label={o.label}
                    description={o.description}
                    icon={o.icon}
                    onClick={() => {
                      setValues({
                        ...values,
                        accommodation: o.value
                      });
                    }}
                    selected={o.value === values.accommodation}
                    readonly={isReadonly("accommodation")}
                  />
                ))}
              </CardsContainer>
            )}
          </Form.Item>
          <FormGroup>
            <Form.Item
              label={t(`wizard.steps.AboutSpace.form.rent_area.title`)}
              validateStatus={errors?.rent_area ? "error" : ""}
              help={errors?.rent_area ? t("wizard.errors.generic") : ""}
            >
              {viewonly ? (
                <div>{values.rent_area ?? ""}</div>
              ) : (
                <Input
                  size={inputSize}
                  disabled={isReadonly("rent_area")}
                  value={values.rent_area ?? undefined}
                  placeholder={t(
                    `wizard.steps.AboutSpace.form.rent_area.placeholder`
                  )}
                  type={"number"}
                  suffix={"m2"}
                  onChange={(e) => {
                    setValues({
                      ...values,
                      rent_area: e.target.value
                    });
                  }}
                />
              )}
            </Form.Item>
            <Form.Item
              label={t(`wizard.steps.AboutSpace.form.entire_house_area.title`)}
              validateStatus={errors?.entire_house_area ? "error" : ""}
              help={errors?.entire_house_area ? t("wizard.errors.generic") : ""}
            >
              {viewonly ? (
                <div>{values.entire_house_area ?? ""}</div>
              ) : (
                <Input
                  size={inputSize}
                  disabled={isReadonly("entire_house_area")}
                  value={values.entire_house_area ?? undefined}
                  placeholder={t(
                    `wizard.steps.AboutSpace.form.entire_house_area.placeholder`
                  )}
                  type={"number"}
                  suffix={"m2"}
                  onChange={(e) => {
                    setValues({
                      ...values,
                      entire_house_area: e.target.value
                    });
                  }}
                />
              )}
            </Form.Item>
            <Form.Item
              label={t(`wizard.steps.AboutSpace.form.amount_of_people.title`)}
              validateStatus={errors?.amount_of_people ? "error" : ""}
              help={errors?.amount_of_people ? t("wizard.errors.generic") : ""}
            >
              {viewonly ? (
                <div>
                  {
                    amountOfPeopleOptions.find(
                      (v) => v.value === values.amount_of_people
                    )?.label
                  }
                </div>
              ) : (
                <Select
                  value={values.amount_of_people ?? undefined}
                  options={amountOfPeopleOptions}
                  size="large"
                  disabled={isReadonly("amount_of_people")}
                  onChange={(val) => {
                    setValues({
                      ...values,
                      amount_of_people: val
                    });
                  }}
                />
              )}
            </Form.Item>
          </FormGroup>
          <Form.Item
            label={t(`wizard.steps.AboutSpace.form.private_items.title`)}
            validateStatus={errors?.private_items ? "error" : ""}
            help={errors?.private_items ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <div>
                {privateUseOptions
                  .filter((c) => values.private_items?.includes(c.value))
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </div>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("private_items")}
                value={values.private_items ?? undefined}
                options={privateUseOptions}
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    private_items: newValue
                  })
                }
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.AboutSpace.form.shared_items.title`)}
            validateStatus={errors?.shared_items ? "error" : ""}
            help={errors?.shared_items ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <div>
                {sharedUseOptions
                  .filter((c) => values.shared_items?.includes(c.value))
                  ?.map((o) => o.label)
                  .join(", ") ?? ""}
              </div>
            ) : (
              <WizardButtonSelect
                disabled={isReadonly("shared_items")}
                value={values.shared_items ?? undefined}
                options={sharedUseOptions}
                gridTemplate="1fr 1fr"
                onChange={(newValue) =>
                  setValues({
                    ...values,
                    shared_items: newValue
                  })
                }
              />
            )}
          </Form.Item>
        </Form>
      );
    }
  );
